import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Box,
  useTheme,
  alpha,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { useLocationCtx } from "../context/LocationContext";
import { getPremiumPricesByCountry } from "../utils/premiumPricing";
import { trackEvent } from "../utils/analytics/gtag";

const categories = [
  { value: "bug", icon: <BugReportOutlinedIcon /> },
  { value: "idea", icon: <LightbulbOutlinedIcon /> },
  { value: "confusion", icon: <HelpOutlineOutlinedIcon /> },
  { value: "pricing", icon: <PaidOutlinedIcon /> },
];

function getNicePageLabel(pathname) {
  if (!pathname) return "Home";
  return pathname === "/" ? "Home" : pathname;
}

export default function FeedbackModal({ open, onClose }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { country } = useLocationCtx();

  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [pricingIntent, setPricingIntent] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" });

  const pageLabel = useMemo(() => getNicePageLabel(window.location.pathname), []);
  const premiumPrices = useMemo(() => getPremiumPricesByCountry(country), [country]);

  const selected = categories.find((c) => c.value === category) || categories[0];
  const minChars = category === "bug" ? 6 : 8;
  const canSend =
    message.trim().length >= minChars &&
    (category !== "pricing" || !!pricingIntent) &&
    !loading;

  const handleClose = () => {
    if (loading) return;
    setStatus({ type: "", text: "" });
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSend) {
      setStatus({ type: "warning", text: t("feedback.validation_min_chars", { n: minChars }) });
      return;
    }

    setLoading(true);
    setStatus({ type: "", text: "" });

    try {
      await api.feedback.submit({
        category,
        message,
        page_url: window.location.pathname,
        email: email.trim() || null,
        pricing_intent: category === "pricing" ? pricingIntent : null,
      });


      setStatus({ type: "success", text: t("feedback.success") });
      trackEvent("feedback_submit", {
        category,
        has_email: !!email.trim(),
        page_path: window.location.pathname,
        pricing_intent: category === "pricing" ? pricingIntent : null,
      });
      setMessage("");
      setEmail("");
      setPricingIntent("");

      setTimeout(() => {
        setStatus({ type: "", text: "" });
        onClose();
      }, 900);
    } catch {
      setStatus({ type: "error", text: t("feedback.error") });
    } finally {
      setLoading(false);
    }
  };

  const isEmailValid = (v) => {
    if (!v) return true; // optional
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  };

  const emailOk = isEmailValid(email);


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha("#000", 0.72)
              : alpha("#0B1220", 0.42),
          backdropFilter: "blur(6px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 16px 60px rgba(0,0,0,0.22)",
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.92)}, ${alpha(
                theme.palette.background.default,
                0.92
              )})`
              : `linear-gradient(180deg, #fff, ${alpha(theme.palette.primary.light, 0.08)})`,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.25,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.22)}, transparent)`
              : `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.16)}, transparent)`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              background: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.25 : 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
            }}
          >
            {selected.icon}
          </Box>

          <Stack spacing={0}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {t("feedback.title")}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              {t("feedback.page_label")} <b>{pageLabel}</b>
            </Typography>
          </Stack>
        </Stack>

        <IconButton onClick={handleClose} disabled={loading} aria-label={t("feedback.close")}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.25 }}>
        <Stack spacing={2}>
          {status.text ? <Alert severity={status.type}>{status.text}</Alert> : null}

          <TextField
            select
            label={t("feedback.type_label")}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          >
            {categories.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ display: "grid", placeItems: "center" }}>
                    {c.value === "bug" ? <BugReportOutlinedIcon /> : null}
                    {c.value === "idea" ? <LightbulbOutlinedIcon /> : null}
                    {c.value === "confusion" ? <HelpOutlineOutlinedIcon /> : null}
                    {c.value === "pricing" ? <PaidOutlinedIcon /> : null}
                  </Box>
                  <span>{t(`feedback.categories.${c.value}`)}</span>
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          {category === "pricing" ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                background: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.04),
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                {t("feedback.pricing_question_title")}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                {t("feedback.pricing_question_body", {
                  price_month: premiumPrices.priceMonth,
                  price_year: premiumPrices.priceYear,
                })}
              </Typography>
              <RadioGroup
                row
                value={pricingIntent}
                onChange={(e) => setPricingIntent(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio size="small" />} label={t("feedback.pricing_answers.yes")} />
                <FormControlLabel value="no" control={<Radio size="small" />} label={t("feedback.pricing_answers.no")} />
                <FormControlLabel value="maybe" control={<Radio size="small" />} label={t("feedback.pricing_answers.maybe")} />
              </RadioGroup>
            </Box>
          ) : null}

          <TextField
            label={t("feedback.message_label")}
            placeholder={t("feedback.message_placeholder")}
            multiline
            minRows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
          />

          <TextField
            label={t("feedback.email_label")}
            placeholder={t("feedback.email_placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            error={!emailOk}
            helperText={!emailOk ? t("feedback.email_invalid") : " "}
          />


          <Stack direction="row" spacing={1.25} justifyContent="flex-end">
            <Button onClick={handleClose} disabled={loading}>
              {t("feedback.cancel")}
            </Button>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSend}
              startIcon={loading ? <CircularProgress size={18} /> : <SendRoundedIcon />}
              sx={{
                borderRadius: 999,
                px: 2.5,
                py: 1.1,
                fontWeight: 800,
                textTransform: "none",
              }}
            >
              {t("feedback.send")}
            </Button>
          </Stack>

          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {t("feedback.privacy_note")}
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
